import {
  useMemo,
  useState,
} from 'react';

import {
  Link,
  useNavigate,
} from 'react-router-dom';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LaunchIcon from '@mui/icons-material/Launch';
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
} from '@mui/x-data-grid';
import type {
  OnboardingAdmin,
  OnboardingStatus,
} from '@scriptpad/shared';
import {
  colors,
  TOTAL_STEPS,
} from '@scriptpad/shared';
import { useQuery } from '@tanstack/react-query';

import { listOnboardings } from '../api/client.js';

const STATUS_FILTERS: Array<{ value: '' | OnboardingStatus; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'expired', label: 'Expired' },
];

const statusColor = (s: OnboardingStatus): 'default' | 'info' | 'success' | 'warning' => {
  switch (s) {
    case 'completed':
      return 'success';
    case 'in_progress':
      return 'info';
    case 'expired':
      return 'warning';
    default:
      return 'default';
  }
};

export const OnboardingsListPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'' | OnboardingStatus>('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin-onboardings', status, q, page, pageSize],
    queryFn: () => listOnboardings({ status: status || undefined, q: q || undefined, page: page + 1, pageSize }),
  });

  const columns = useMemo<GridColDef<OnboardingAdmin>[]>(
    () => [
      {
        field: 'invitee',
        headerName: 'Invitee',
        flex: 1.4,
        valueGetter: (_v, row) => `${row.invitee.firstName} ${row.invitee.lastName}`,
        renderCell: (p) => (
          <Stack sx={{ py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {p.row.invitee.firstName} {p.row.invitee.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {p.row.invitee.email}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.7,
        renderCell: (p) => (
          <Chip size="small" color={statusColor(p.row.status)} label={p.row.status.replace('_', ' ')} />
        ),
      },
      {
        field: 'currentStep',
        headerName: 'Step',
        width: 90,
        renderCell: (p) => `${p.row.currentStep} / ${TOTAL_STEPS}`,
      },
      {
        field: 'practiceMode',
        headerName: 'Practice',
        flex: 0.8,
        valueGetter: (_v, row) =>
          row.practiceMode === 'organisation'
            ? 'Organisation'
            : row.practiceMode === 'sole_practitioner'
              ? 'Sole practitioner'
              : '-',
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        flex: 0.8,
        valueGetter: (_v, row) => row.createdAt,
        renderCell: (p) => new Date(p.row.createdAt).toLocaleDateString('en-AU'),
      },
      {
        field: 'lastActivityAt',
        headerName: 'Last activity',
        flex: 1,
        valueGetter: (_v, row) => row.lastActivityAt,
        renderCell: (p) => new Date(p.row.lastActivityAt).toLocaleString('en-AU'),
      },
      {
        field: 'magicLinkUrl',
        headerName: 'Magic link',
        flex: 0.6,
        sortable: false,
        renderCell: (p) => (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Copy link">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    void navigator.clipboard.writeText(p.row.magicLinkUrl);
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open in new tab">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(p.row.magicLinkUrl, '_blank', 'noopener');
                  }}
                >
                  <LaunchIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        ),
      },
    ],
    [],
  );

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="h5">Onboardings</Typography>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" component={Link} to="/onboardings/new">
          New onboarding
        </Button>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          select
          label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as '' | OnboardingStatus);
            setPage(0);
          }}
          sx={{ width: { xs: '100%', sm: 200 }, backgroundColor: "background.paper" }}
          size="small"
        >
          {STATUS_FILTERS.map((f) => (
            <MenuItem key={f.value} value={f.value}>
              {f.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Search by name, email, or GUID"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(0);
          }}
          sx={{ flex: 1, backgroundColor: "background.paper" }}
          size="small"
        />
        <Button onClick={() => refetch()} size="small" sx={{ height: '40px' }}>Refresh</Button>
      </Stack>

      <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, border: `1px solid ${colors.border}` }}>
        <DataGrid<OnboardingAdmin>
          rows={data?.items ?? []}
          getRowId={(row) => row._id}
          columns={columns}
          loading={isLoading}
          rowCount={data?.total ?? 0}
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(m) => {
            setPage(m.page);
            setPageSize(m.pageSize);
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          getRowHeight={() => 64}
          disableRowSelectionOnClick
          onRowClick={(p) => navigate(`/onboardings/${p.row._id}`)}
          sx={{ border: 0, '& .MuiDataGrid-cell': { cursor: 'pointer' } }}
        />
      </Box>
    </Stack>
  );
};
